/**
 * 按钮
 * 尺寸和配色对应设计稿 §03 组件库里的 .btn（pri / sec / dgr，sm / md / lg）。
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  /**
   * 主操作：一屏最多一个，比如「确认收款」。
   * 实底用 brand-2 而不是 brand：白字放在 brand(#C25C31) 上只有 4.31:1，
   * 达不到 WCAG AA 对普通文字的 4.5:1（按钮字号 12–14.5px 都算普通文字）。
   * brand-2 是 6.36:1，hover 的 brand-4 是 8.72:1。
   * brand 仍然是品牌色，用在标识、选中态和图表上，只是不再做按钮实底。
   */
  primary: 'bg-brand-2 text-white shadow-brand hover:bg-brand-4',
  // 次操作：白底描边
  secondary:
    'bg-panel border border-line text-ink shadow-e1 hover:border-[#D3CEC2] hover:bg-panel-2',
  // 危险操作：退款、作废、删除
  danger: 'bg-red-fill text-white hover:brightness-95',
  // 无边框，用在卡片头部的「查看全部 →」这类弱操作
  ghost: 'text-brand-2 hover:text-brand hover:bg-brand-pale',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'h-[31px] px-3 text-form rounded-sm',
  md: 'h-9 px-4 text-body rounded-btn',
  lg: 'h-11 px-[22px] text-card-title rounded-tile',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 按钮左侧图标 */
  icon?: IconName;
  /** 占满父容器宽度 */
  block?: boolean;
  /**
   * 提交中：自动禁用并把文案换成加载态。
   * 收款、充值这类会产生真实交易的按钮必须用它防连点。
   */
  loading?: boolean;
  children?: ReactNode;
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  block,
  loading,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 font-semibold whitespace-nowrap transition-[background-color,border-color,color,box-shadow] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        icon && <Icon name={icon} size={size === 'sm' ? 13 : 15} />
      )}
      {children}
    </button>
  );
}

/**
 * 纯图标按钮（顶栏的通知、帮助）。
 * 必须传 label，它会变成 aria-label 和 title，键盘和读屏用户才知道这是什么。
 */
export function IconButton({
  icon,
  label,
  badge,
  className,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  icon: IconName;
  label: string;
  /** 右上角小红点数字，如未读通知数 */
  badge?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'relative grid size-[33px] place-items-center rounded-btn border border-line bg-panel text-ink-2 shadow-e1',
        'hover:border-[#D6D1C5] hover:text-ink',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={16} />
      {badge !== undefined && badge > 0 && (
        <i className="absolute -top-1 -right-1 grid h-[15px] min-w-[15px] place-items-center rounded-full border-[1.5px] border-paper bg-red-fill px-1 text-[9px] font-bold text-white not-italic">
          {badge > 99 ? '99+' : badge}
        </i>
      )}
    </button>
  );
}
